import {
  FIXED_DATE_GREETINGS,
  EVENING_DATE_GREETINGS,
  EID_DATES,
  EID_GREETING_TEMPLATE,
  STAFF_BIRTHDAYS,
  BIRTHDAY_GREETING_TEMPLATE,
  ZEENA_EMAIL,
  ZEENA_EASTER_EGGS,
  ZEENA_EASTER_EGG_CHANCE,
} from './greetingConfig'

/** Short relative time for the Portal activity feed. */
export function relativeTime(at: number): string {
  const minutes = Math.round((Date.now() - at) / 60000)
  if (minutes < 1) return 'Just now'
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`
  const days = Math.round(hours / 24)
  if (days === 1) return 'Yesterday'
  if (days < 7) return `${days} days ago`
  return `${Math.round(days / 7)} week${days < 14 ? '' : 's'} ago`
}

function parseISO(iso: string): Date | null {
  if (!iso) return null
  const parsed = new Date(`${iso}T00:00:00`)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

/** "Friday 25 September" */
export function formatShootDate(iso: string): string {
  const date = parseISO(iso)
  if (!date) return ''
  return date.toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  })
}

export function shootDay(iso: string): string {
  const date = parseISO(iso)
  return date ? String(date.getDate()).padStart(2, '0') : '—'
}

export function shootMonth(iso: string): string {
  const date = parseISO(iso)
  return date ? date.toLocaleDateString('en-GB', { month: 'short' }).toUpperCase() : ''
}

export function shootYear(iso: string): string {
  const date = parseISO(iso)
  return date ? String(date.getFullYear()) : ''
}

/** A shoot stops being "next" once its date has passed. */
export function isUpcoming(iso: string): boolean {
  const date = parseISO(iso)
  if (!date) return false
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return date.getTime() >= today.getTime()
}

/**
 * Accepts "17", "00:17", "1:24", "1:02:03" and normalises to video timecode.
 * Returns '' when nothing usable was entered.
 */
export function normaliseTimecode(input: string): string {
  const value = (input ?? '').trim()
  if (!value) return ''

  const parts = value.split(':').map((part) => part.trim())
  if (parts.some((part) => part === '' || !/^\d+$/.test(part)) || parts.length > 3) return ''

  const numbers = parts.map(Number)
  let total = 0
  if (numbers.length === 1) total = numbers[0]
  else if (numbers.length === 2) total = numbers[0] * 60 + numbers[1]
  else total = numbers[0] * 3600 + numbers[1] * 60 + numbers[2]

  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const seconds = total % 60
  const mm = String(minutes).padStart(2, '0')
  const ss = String(seconds).padStart(2, '0')
  return hours > 0 ? `${hours}:${mm}:${ss}` : `${mm}:${ss}`
}

/** "25 AUG · 5:16 PM" in the viewer's own timezone, for history rows. */
export function formatRevisionStamp(at: number): string {
  const date = new Date(at)
  const day = date
    .toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
    .toUpperCase()
  const time = date.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
  return `${day} · ${time}`
}

/** "25 Aug, 3:47 PM" in the viewer's own timezone. */
export function formatEditStamp(at: number): string {
  const date = new Date(at)
  const day = date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
  const time = date.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
  return `${day}, ${time}`
}

/**
 * The current date/time in Asia/Dubai, regardless of the viewer's own
 * device timezone. All greeting logic in this file is built on this —
 * never on `now.getHours()` / `now.getDate()` directly, which read the
 * VIEWER's local clock.
 *
 * `minutesSince3am` re-bases the clock so 3:00am (the start of the
 * "morning" window) is minute 0 of a 1440-minute day. Every staff
 * time-of-day window below is a single, non-wrapping range in this
 * space, which is what lets "5pm–2:59am"-style overnight windows be
 * expressed without special-casing midnight.
 */
function dubaiNow(now: Date = new Date()) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Dubai',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(now)
  const get = (type: string) => Number(parts.find((part) => part.type === type)?.value ?? 0)

  const year = get('year')
  const month = get('month')
  const day = get('day')
  // Some environments render midnight as hour "24" with hour12: false —
  // normalise it back to 0.
  const hour = get('hour') % 24
  const minute = get('minute')
  const minutesSince3am = ((hour - 3 + 24) % 24) * 60 + minute

  return { year, month, day, hour, minute, minutesSince3am }
}

/**
 * The client portal's greeting: always just the time of day plus the
 * client's own name, in Asia/Dubai time. No rotation, no overrides —
 * clients never see the staff rotation, birthdays, calendar messages,
 * or Zeena's easter eggs.
 */
export function clientGreeting(name: string, now: Date = new Date()): string {
  const { minutesSince3am } = dubaiNow(now)
  const word =
    minutesSince3am < 540 ? 'GOOD MORNING' : minutesSince3am < 840 ? 'GOOD AFTERNOON' : 'GOOD EVENING'
  const trimmed = (name ?? '').trim()
  return trimmed ? `${word}, ${trimmed}` : word
}

/** One candidate in a random-selection pool. */
interface GreetingOption {
  id: string
  template: string
}

const STAFF_TIME_GREETINGS: (GreetingOption & { from: number; to: number })[] = [
  { id: 'good-morning', template: 'GOOD MORNING, (NAME)', from: 0, to: 540 },
  { id: 'morning', template: 'MORNING, (NAME)', from: 0, to: 540 },
  { id: 'good-afternoon', template: 'GOOD AFTERNOON, (NAME)', from: 540, to: 840 },
  { id: 'afternoon', template: 'AFTERNOON, (NAME)', from: 540, to: 840 },
  { id: 'good-evening', template: 'GOOD EVENING, (NAME)', from: 840, to: 1440 },
  { id: 'evening', template: 'EVENING, (NAME)', from: 840, to: 1440 },
  { id: 'early-start', template: 'EARLY START, (NAME)?', from: 120, to: 241 }, // 5:00am–7:00am inclusive
  { id: 'late-one', template: 'LATE ONE, (NAME)?', from: 1080, to: 1440 }, // from 9:00pm
  { id: 'after-hours', template: 'AFTER HOURS, (NAME)', from: 1080, to: 1440 }, // from 9:00pm
  { id: 'night-shift', template: 'THE NIGHT SHIFT, (NAME)', from: 1200, to: 1440 }, // from 11:00pm
  { id: 'portal-never-sleeps', template: 'THE PORTAL NEVER SLEEPS, (NAME)', from: 1200, to: 1440 }, // from 11:00pm
]

const LAST_GREETING_KEY = 'portal.staff.lastGreetingId'

function readLastGreetingId(): string {
  try {
    return window.localStorage.getItem(LAST_GREETING_KEY) ?? ''
  } catch {
    return ''
  }
}

function writeLastGreetingId(id: string): void {
  try {
    window.localStorage.setItem(LAST_GREETING_KEY, id)
  } catch {
    /* Storage unavailable — repeat prevention simply won't apply this time. */
  }
}

/** Excludes the previous greeting from a pool of 2+, remembers whichever
    id is finally chosen, and returns a random pick. */
function pickWithRepeatPrevention(pool: GreetingOption[]): GreetingOption {
  const lastId = readLastGreetingId()
  const candidates = pool.length > 1 ? pool.filter((option) => option.id !== lastId) : pool
  const chosen = candidates[Math.floor(Math.random() * candidates.length)] ?? pool[0]
  writeLastGreetingId(chosen.id)
  return chosen
}

function eligibleCalendarGreetings(d: ReturnType<typeof dubaiNow>): GreetingOption[] {
  const pool: GreetingOption[] = []

  for (const rule of FIXED_DATE_GREETINGS) {
    if (rule.month === d.month && rule.day === d.day) {
      for (const template of rule.templates) pool.push({ id: rule.id, template })
    }
  }

  for (const rule of EVENING_DATE_GREETINGS) {
    if (rule.month === d.month && rule.day === d.day && d.minutesSince3am >= rule.fromMinutesSince3am) {
      for (const template of rule.templates) pool.push({ id: rule.id, template })
    }
  }

  const iso = `${d.year}-${String(d.month).padStart(2, '0')}-${String(d.day).padStart(2, '0')}`
  if (EID_DATES.includes(iso)) pool.push({ id: 'eid', template: EID_GREETING_TEMPLATE })

  return pool
}

function eligibleTimeGreetings(d: ReturnType<typeof dubaiNow>): GreetingOption[] {
  return STAFF_TIME_GREETINGS.filter(
    (option) => d.minutesSince3am >= option.from && d.minutesSince3am < option.to,
  ).map(({ id, template }) => ({ id, template }))
}

function fillName(template: string, name: string): string {
  return template.replace('(NAME)', name)
}

/**
 * The staff dashboard's greeting. Priority order:
 *   1. Birthday (if today matches this staff member's configured
 *      birthday) — always wins, overrides everything else below.
 *   2. A calendar/date-specific greeting, if one is active today.
 *   3. For Zeena only, a rare (~10%) fully off-script easter egg.
 *   4. Otherwise, the normal time-of-day rotation, avoiding an
 *      immediate repeat of the last greeting shown where possible.
 *
 * Call this once per page view (see the useMemo in Projects.tsx) — it is
 * randomised and is not safe to call fresh on every render.
 */
export function staffGreeting(
  staffUser: { name?: string; email?: string } | null | undefined,
  now: Date = new Date(),
): string {
  const name = firstName(staffUser?.name)
  const email = (staffUser?.email ?? '').trim().toLowerCase()
  const d = dubaiNow(now)

  const birthday = email ? STAFF_BIRTHDAYS[email] : undefined
  if (birthday && birthday.month === d.month && birthday.day === d.day) {
    return fillName(BIRTHDAY_GREETING_TEMPLATE, name)
  }

  const calendarPool = eligibleCalendarGreetings(d)
  if (calendarPool.length > 0) {
    const chosen = calendarPool[Math.floor(Math.random() * calendarPool.length)]
    return fillName(chosen.template, name)
  }

  const isZeena = email !== '' && email === ZEENA_EMAIL.trim().toLowerCase()
  if (isZeena && Math.random() < ZEENA_EASTER_EGG_CHANCE) {
    const chosen = pickWithRepeatPrevention(
      ZEENA_EASTER_EGGS.map((egg) => ({ id: egg.id, template: egg.text })),
    )
    return chosen.template // literal line, no (NAME) substitution
  }

  const chosen = pickWithRepeatPrevention(eligibleTimeGreetings(d))
  return fillName(chosen.template, name)
}

/**
 * First name only, from a full name — never a fixed name. Shared by the
 * staff nav (PortalApp.tsx) and the staff greeting above so both derive
 * it the same way.
 */
export function firstName(name: string | undefined): string {
  const first = (name ?? '').trim().split(/\s+/)[0]
  return first || 'Staff'
}

export const MINUTE = 60000
export const HOUR = 3600000
export const DAY = 86400000
